import { useEffect, useMemo, useRef, useState } from "react";
import CommonModal from "../shared/modalLayout/CommonModal";
import Button from "../shared/buttons/button";
import { SearchSvg } from "../../svgFiles/SearchSvg";
import { CrossSvg } from "../../svgFiles/CrossSvg";
import "../../features/admin/va-distributors/vaDistributors.css";

const TABS = [
  { id: "all", label: "All" },
  { id: "assigned", label: "Assigned" },
  { id: "unassigned", label: "Unassigned" },
];

function formatHospitalLabel(hospital) {
  const name = hospital?.name || "Hospital";
  return hospital?.city ? `${name} (${hospital.city})` : name;
}

function isHospitalLocked(hospital, currentDistributorId) {
  const ownerId = hospital?.assignedDistributorId
    ? String(hospital.assignedDistributorId)
    : "";
  if (!ownerId) return false;
  const selfId = currentDistributorId ? String(currentDistributorId) : "";
  return !selfId || ownerId !== selfId;
}

function StateCheckbox({ checked, indeterminate, onChange, label, disabled }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <label
      className={`select-hospitals-row select-hospitals-row--state${
        disabled ? " is-disabled" : ""
      }`}
    >
      <input
        ref={ref}
        type="checkbox"
        className="select-hospitals-check"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span className="select-hospitals-row__name">{label}</span>
    </label>
  );
}

export default function SelectHospitalsModal({
  show,
  setShow,
  hospitalIds = [],
  onConfirm,
  stateGroups = [],
  isLoading = false,
  currentDistributorId = null,
  hideAssignedTab = false,
}) {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all");
  const [draftIds, setDraftIds] = useState([]);

  const tabs = useMemo(
    () => (hideAssignedTab ? TABS.filter((item) => item.id !== "assigned") : TABS),
    [hideAssignedTab]
  );

  useEffect(() => {
    if (!show) return;
    setQuery("");
    setTab("all");
    setDraftIds(
      Array.isArray(hospitalIds) ? hospitalIds.map(String).filter(Boolean) : []
    );
  }, [show, hospitalIds]);

  const selectedSet = useMemo(() => new Set(draftIds.map(String)), [draftIds]);

  const filteredGroups = useMemo(() => {
    const term = query.trim().toLowerCase();

    return (stateGroups || [])
      .map((group) => {
        const hospitals = (group.hospitals || []).filter((hospital) => {
          const id = String(hospital.id);
          const selected = selectedSet.has(id);
          const locked = isHospitalLocked(hospital, currentDistributorId);
          if (tab === "assigned" && !selected) return false;
          // Unassigned = free for this distributor (not selected here, not owned by another)
          if (tab === "unassigned" && (selected || locked)) return false;
          if (!term) return true;
          const haystack = [
            hospital.name,
            hospital.city,
            hospital.assignedDistributorName,
            group.stateName,
            group.stateCode,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        });
        return { ...group, hospitals };
      })
      .filter((group) => (group.hospitals || []).length > 0);
  }, [stateGroups, query, tab, selectedSet, currentDistributorId]);

  const toggleHospital = (hospital) => {
    const id = String(hospital.id);
    if (isHospitalLocked(hospital, currentDistributorId)) return;
    setDraftIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const toggleState = (group) => {
    const selectable = (group.hospitals || []).filter(
      (hospital) => !isHospitalLocked(hospital, currentDistributorId)
    );
    const ids = selectable.map((hospital) => String(hospital.id));
    if (!ids.length) return;
    const allSelected = ids.every((id) => selectedSet.has(id));
    setDraftIds((prev) => {
      if (allSelected) {
        const remove = new Set(ids);
        return prev.filter((id) => !remove.has(id));
      }
      return [...new Set([...prev, ...ids])];
    });
  };

  const handleClose = () => setShow?.(false);

  const handleConfirm = () => {
    onConfirm?.([...draftIds]);
    handleClose();
  };

  const selectedCount = draftIds.length;

  return (
    <CommonModal
      show={show}
      setShow={setShow}
      title=""
      className="select-hospitals-modal"
      centered
      headerActions={
        <div className="select-hospitals-modal__search">
          <SearchSvg />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Select Hospitals"
            aria-label="Search hospitals"
          />
          {query ? (
            <button
              type="button"
              className="select-hospitals-modal__clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              <CrossSvg />
            </button>
          ) : null}
        </div>
      }
      footer={
        <div className="select-hospitals-modal__footer">
          <span className="select-hospitals-modal__count">
            {selectedCount} hospital{selectedCount === 1 ? "" : "s"} selected
          </span>
          <div className="d-flex gap-2">
            <Button
              label="Cancel"
              className="cancel"
              onClick={handleClose}
            />
            <Button label="Confirm" onClick={handleConfirm} />
          </div>
        </div>
      }
    >
      <div className="select-hospitals-tabs" role="tablist">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`select-hospitals-tab${
              tab === item.id ? " is-active" : ""
            }`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="select-hospitals-list-head">
        <span>Hospital</span>
        <span>Status</span>
      </div>

      <div className="select-hospitals-list">
        {isLoading ? (
          <p className="select-hospitals-list__empty">Loading hospitals…</p>
        ) : filteredGroups.length === 0 ? (
          <p className="select-hospitals-list__empty">No hospitals found.</p>
        ) : (
          filteredGroups.map((group) => {
            const selectable = (group.hospitals || []).filter(
              (hospital) => !isHospitalLocked(hospital, currentDistributorId)
            );
            const ids = selectable.map((hospital) => String(hospital.id));
            const selectedCountInState = ids.filter((id) =>
              selectedSet.has(id)
            ).length;
            const allSelected =
              ids.length > 0 && selectedCountInState === ids.length;
            const someSelected = selectedCountInState > 0 && !allSelected;
            const stateDisabled = ids.length === 0;

            return (
              <div key={group.stateCode} className="select-hospitals-group">
                <div className="select-hospitals-grid-row">
                  <StateCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={stateDisabled}
                    onChange={() => toggleState(group)}
                    label={group.stateName || group.stateCode}
                  />
                  <span />
                </div>
                {(group.hospitals || []).map((hospital) => {
                  const id = String(hospital.id);
                  const assigned = selectedSet.has(id);
                  const locked = isHospitalLocked(
                    hospital,
                    currentDistributorId
                  );
                  const ownerName =
                    hospital.assignedDistributorName || "another distributor";

                  return (
                    <div key={id} className="select-hospitals-grid-row">
                      <label
                        className={`select-hospitals-row select-hospitals-row--hospital${
                          locked ? " is-disabled" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="select-hospitals-check"
                          checked={assigned}
                          disabled={locked}
                          onChange={() => toggleHospital(hospital)}
                        />
                        <span className="select-hospitals-row__name">
                          {formatHospitalLabel(hospital)}
                        </span>
                      </label>
                      <span className="select-hospitals-status">
                        {locked ? (
                          <span
                            className="select-hospitals-status__badge select-hospitals-status__badge--locked"
                            title={`Assigned to ${ownerName}`}
                          >
                            {ownerName}
                          </span>
                        ) : assigned ? (
                          <span className="select-hospitals-status__badge">
                            Assigned
                          </span>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </CommonModal>
  );
}
